import {Box, Button, FormHelperText, Grid, useMediaQuery} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import AccountRouteConstants from 'Agent/Accounts/AccountRouteConstants';
import {PaymentDto, PendingPaymentDto, Source} from 'Agent/Accounts/api';
import {Invoice} from 'Agent/Accounts/models/Invoice';
import 'Agent/Policies/components/PolicyImagesCard.sass';
import React, {useMemo} from 'react';
import {useHistory, useRouteMatch} from 'react-router-dom';
import {AllstateCardV2} from 'Shared/components/AllstateCardV2';
import {CardFieldV2} from 'Shared/components/CardFieldV2';
import FileDownloadButton from 'Shared/components/FileDownloadButton';
import {SwipeableView} from 'Shared/components/SwipeableView';
import {toFormattedDateAsUTC} from 'Shared/utilities/DateFormatter';
import {toMoneyValue} from 'Shared/utilities/FormatMoney';
import {UserRouteConstants} from 'Users/Routes';
import {GetAppSharp} from '@material-ui/icons';
import {RequestMethod} from 'Shared/utilities/fetchHelpers';
import { fromJS } from 'immutable';

export const useGridStyles = makeStyles(theme => ({
  downloadFileBtn: {
    borderRadius: '6.25rem',
    width: '11.5rem',
    padding: '0.65rem',
    backgroundColor: '#DAEDFE',
    boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
    color: '#2f3847',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: '#0033A0',
      color: '#ffffff',
    },
  },
  header: {
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      display: 'none',
      marginBottom: '2rem',
    },
    [theme.breakpoints.down('sm')]: {
      paddingRight: '1rem',
    },
  },
  container: {
    margin: '0',
    width: '100%',
  },
  innerGrid: {
    alignItems: 'center',
    borderBottom: 'none',
    '&:last-of-type': {
      borderBottom: 'none',
    },
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      padding: '.5rem',
    },
    [theme.breakpoints.down('sm')]: {
      paddingRight: '1rem',
    },
  },
  cardFieldWithoutLabel: {
    display: 'none',
  },
  gridContainer: {
    padding: '1rem',
    width: '100%',
    margin: 0,
    justifyContent: 'flex-start',
  },
  stepper: {
    backgroundColor: 'transparent',
    paddingBottom: '1rem',
  },
  frequency: {
    paddingLeft: '1.5rem',
    marginTop: '1rem',
    paddingBottom: '1rem',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: '1.5rem',
    },
    [theme.breakpoints.down('xs')]: {
      paddingLeft: '2rem',
    },
  },
  separator: {
    border: '1.5px solid #ccc',
    width: '100%',
    [theme.breakpoints.down('xs')]: {
      width: '95%',
    },
  },
  viewPaymentDiscrepancyBtn: {
    fontWeight: 700,
    paddingLeft: '0',
    [theme.breakpoints.down('xs')]: {
      padding: '0',
    },
  },
  viewInvoiceDetails: {
    fontWeight: 700,
    [theme.breakpoints.down('xs')]: {
      padding: '0',
    },
  },
}));

type PaymentListItemProps = {
  accountNumber?: string;
  accountSource?: Source;
  accountId?: string;
  payment?: PaymentDto;
  history: any;
  isMobile?: boolean;
  eisFlag: boolean;
};

const PaymentListItem = (props: PaymentListItemProps) => {
  const {accountNumber, accountId, accountSource, payment, history, isMobile, eisFlag} = props;
  const styles = useGridStyles();
  const gridSize = eisFlag ? 3 : 4;
  const fieldWithoutLableStyles = !isMobile ? {name: styles.cardFieldWithoutLabel} : undefined;

  return (
    <Grid container spacing={2} className={`${styles.innerGrid} ${styles.container}`} role="row">
      {eisFlag && (
        <Grid item sm={gridSize} md={gridSize} role="cell">
          <CardFieldV2 name="Payment Number" value={payment?.paymentNumber} styles={fieldWithoutLableStyles} />
        </Grid>
      )}
      <Grid item sm={gridSize} md={gridSize} role="cell">
        <CardFieldV2 name="Date" value={toFormattedDateAsUTC(payment?.date)} styles={fieldWithoutLableStyles} />
      </Grid>
      <Grid item sm={gridSize} md={gridSize} role="cell">
        <CardFieldV2 name="Amount" value={toMoneyValue(payment?.amount)} styles={fieldWithoutLableStyles} />
      </Grid>
      {!eisFlag && (
        <Grid item sm={gridSize} md={gridSize} role="cell">
          {payment?.hasDiscrepancies ? (
            <Button
              color="primary"
              className={styles.viewPaymentDiscrepancyBtn}
              onClick={() => {
                history.push(
                  AccountRouteConstants.ACCOUNT_DISCREPANCY_SUMMARIES(
                    // @ts-ignore
                    accountId,
                    accountNumber ?? '',
                    accountSource?.id.toString() ?? '',
                    payment?.date.toString()
                  )
                );
              }}
            >
              View Discrepancies
            </Button>
          ) : (
            <CardFieldV2 name="Discrepant" value="No" styles={fieldWithoutLableStyles} />
          )}
        </Grid>
      )}
    </Grid>
  );
};

type AccountPaymentProps = {
  eisFlag: boolean;
  accountNumber?: string;
  accountPayments: PaymentDto[];
  accountSource?: Source;
  paymentFrequency?: string;
};

export const Payments = (props: AccountPaymentProps) => {
  const {accountNumber, accountPayments, accountSource, eisFlag, paymentFrequency} = props;
  const match = useRouteMatch();
  const history = useHistory();
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('xs'));
  const styles = useGridStyles();
  const gridSize = eisFlag ? 3 : 4;

  return (
    <AllstateCardV2 title="Payments">
      <Box className={styles.frequency}>
        <CardFieldV2 name={eisFlag ? 'Invoice Frequency' : 'Payment Frequency'} value={paymentFrequency} />
      </Box>
      {isMobile && <hr className={styles.separator} />}
      {isMobile ? (
        <Box style={{padding: '0 1rem'}}>
          <SwipeableView styles={{stepper: styles.stepper}}>
            {accountPayments.map((payment, index) => (
              <PaymentListItem
                key={`payment-${index}-${payment.date}-${payment.amount}`}
                accountNumber={accountNumber}
                accountSource={accountSource}
                history={history}
                isMobile={isMobile}
                payment={payment}
                eisFlag={eisFlag}
                //@ts-ignore
                accountId={match.params.accountId!}
              />
            ))}
          </SwipeableView>
        </Box>
      ) : (
        <Grid container spacing={4} className={styles.container} role="table">
          <Grid item xs={12} md={12} role="presentation">
            <Grid container spacing={2} className={`${styles.header} ${styles.container}`} role="row">
              {eisFlag && (
                <Grid item xs={gridSize} role="columnheader">
                  Payment #
                </Grid>
              )}
              <Grid item xs={gridSize} role="columnheader">
                Date
              </Grid>
              <Grid item xs={gridSize} role="columnheader">
                Amount
              </Grid>
              {!eisFlag && (
                <Grid item xs={gridSize} role="columnheader">
                  Discrepant
                </Grid>
              )}
            </Grid>
            {!isMobile && <hr className={styles.separator} role="presentation" />}
            <Grid container spacing={2} className={styles.container} role="rowgroup">
              {accountPayments.map((payment, index) => (
                <PaymentListItem
                  key={`payment-${index}-${payment.date}-${payment.amount}`}
                  accountNumber={accountNumber}
                  accountSource={accountSource}
                  history={history}
                  isMobile={isMobile}
                  payment={payment}
                  eisFlag={eisFlag}
                  //@ts-ignore
                  accountId={match.params.accountId!}
                />
              ))}
            </Grid>
          </Grid>
        </Grid>
      )}
    </AllstateCardV2>
  );
};

export const PendingPayment = ({pendingPayment}: {pendingPayment?: PendingPaymentDto}) => {
  const {gridContainer} = useGridStyles();
  const formatPendingPaymentDate = (value: string) => (value === '01/01/0001' ? 'N/A' : value);

  return (
    <AllstateCardV2 title="Pending Payment">
      <Grid container spacing={4} className={gridContainer}>
        <Grid item xs={12} sm={6} md={6}>
          <CardFieldV2
            name="Date Created"
            value={formatPendingPaymentDate(toFormattedDateAsUTC(pendingPayment?.processDate))}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <CardFieldV2
            name="Pending Payment Reason"
            value={pendingPayment?.statusDescription
              ?.split('_')
              .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
              .join(' ')}
          />
        </Grid>
      </Grid>
    </AllstateCardV2>
  );
};

type InvoiceListItemProps = {
  accountNumber?: string;
  invoice?: Invoice;
  history: any;
  isMobile?: boolean;
  eisFlag?: boolean;
  queryParams?: string;
};

const InvoiceListItem = ({accountNumber, invoice, history, isMobile, eisFlag, queryParams}: InvoiceListItemProps) => {
  const styles = useGridStyles();
  const gridSize = eisFlag ? 2 : 6;
  const fieldWithoutLableStyles = !isMobile ? {name: styles.cardFieldWithoutLabel} : undefined;

  return (
    <Grid container spacing={2} className={`${styles.innerGrid} ${styles.container}`} role="row">
      {eisFlag && (
        <Grid item sm={gridSize} md={gridSize} role="cell">
          <CardFieldV2 name="Invoice Number" value={invoice?.invoiceNumber} styles={fieldWithoutLableStyles} />
        </Grid>
      )}
      <Grid item sm={gridSize} md={gridSize} role="cell">
        <CardFieldV2 name="Date" value={toFormattedDateAsUTC(invoice?.invoiceDate)} styles={fieldWithoutLableStyles} />
      </Grid>
      <Grid item sm={gridSize} md={gridSize} role="cell">
        <CardFieldV2 name="Delinquent" value={invoice?.isDelinquent ? 'Yes' : 'No'} styles={fieldWithoutLableStyles} />
      </Grid>
      {eisFlag && (
        <Grid item sm={3} md={gridSize} role="cell">
          <CardFieldV2
            name="Status"
            value={invoice?.status
              ?.split('_')
              .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
              .join(' ')}
            styles={fieldWithoutLableStyles}
          />
        </Grid>
      )}
      {eisFlag && (
        <Grid item sm="auto" md={gridSize} lg={gridSize} role="cell">
          <Button
            color="primary"
            className={styles.viewInvoiceDetails}
            onClick={() => {
              history.push(
                AccountRouteConstants.INVOICE_DISCREPANCIES(String(accountNumber), invoice?.link?.href ?? '') +
                  queryParams
              );
            }}
          >
            View Details
          </Button>
        </Grid>
      )}
      {eisFlag && (
        <Grid item sm={3} md={gridSize} lg={gridSize} role="cell" style={{display:"inline-table"}}>
          <FileDownloadButton
            downloadedFileName={'Invoice_' + invoice.documentId}
            flag={true}
            extension={'.pdf'}
            displayTooltip={false}
            relativeFileLocation={'api/employer/v1/broker/invoices/download'}
            className={styles.downloadFileBtn}
            startIcon={<GetAppSharp />}
            requestMethod={RequestMethod.POST}
            requestPayload={fromJS({'externalUrl': invoice.externalUrl})}
          >
            Download Invoice
          </FileDownloadButton>
        </Grid>
      )}
    </Grid>
  );
};

type InvoicesProps = {
  accountInvoices: Invoice[];
  accountNumber?: string;
  eisFlag: boolean;
  parameters?: string;
};

export const Invoices = ({accountInvoices, accountNumber, eisFlag, parameters}: InvoicesProps) => {
  const history = useHistory();
  const styles = useGridStyles();
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('xs'));

  const sortedInvoices = useMemo(() => {
    return accountInvoices.sort(
      (invoice1: Invoice, invoice2: Invoice) =>
        new Date(invoice2.invoiceDate).getTime() - new Date(invoice1.invoiceDate).getTime()
    );
  }, [accountInvoices]);

  const invoiceGridSize = eisFlag ? 2 : 6;

  return (
    <AllstateCardV2 title="Invoices">
      {!eisFlag && (
        <>
          <Box style={{paddingLeft: '0.5rem', marginTop: '1rem'}}>
            <FormHelperText style={{padding: '0rem 1rem .75rem 1rem', fontSize: '14px', color: '#000000DE'}}>
              Access EasyBill Online from the{' '}
              <a
                style={{cursor: 'pointer', fontWeight: 'bold', color: '#0033A0'}}
                onClick={() => {
                  history.push(UserRouteConstants.SSO_ACCOUNTS);
                }}
              >
                <u>Single Sign On</u>
              </a>{' '}
              page to view or download your invoices.
            </FormHelperText>
          </Box>
          {isMobile && <hr className={styles.separator} />}
        </>
      )}
      {isMobile ? (
        <Box style={{padding: '0 1rem'}}>
          <SwipeableView styles={{stepper: styles.stepper}}>
            {sortedInvoices.map((invoice, index) => (
              <InvoiceListItem
                key={`invoice-${index}-${invoice.documentId}`}
                accountNumber={accountNumber}
                invoice={invoice}
                history={history}
                isMobile={isMobile}
                eisFlag={eisFlag}
                queryParams={parameters}
              />
            ))}
          </SwipeableView>
        </Box>
      ) : (
        <Grid container spacing={4} className={styles.container}>
          <Grid item xs={12} md={12} role="table">
            <Grid container spacing={2} className={`${styles.header} ${styles.container}`} role="row">
              {eisFlag && (
                <Grid item xs={invoiceGridSize} role="columnheader">
                  Invoice #
                </Grid>
              )}
              <Grid item xs={invoiceGridSize} role="columnheader">
                Bill Date
              </Grid>
              <Grid item xs={invoiceGridSize} role="columnheader">
                Delinquent
              </Grid>
              {eisFlag && (
                <Grid item xs={invoiceGridSize} role="columnheader">
                  Status
                </Grid>
              )}
              {eisFlag && <Grid item xs={invoiceGridSize} />}
              {eisFlag && <Grid item xs={invoiceGridSize} />}
            </Grid>
            {!isMobile && <hr className={styles.separator} role="presentation" />}
            <Grid container spacing={2} className={styles.container} role="rowgroup">
              {sortedInvoices.map((invoice, index) => (
                <InvoiceListItem
                  key={`invoice-${index}-${invoice.documentId}`}
                  accountNumber={accountNumber}
                  invoice={invoice}
                  history={history}
                  isMobile={isMobile}
                  eisFlag={eisFlag}
                  queryParams={parameters}
                />
              ))}
            </Grid>
          </Grid>
        </Grid>
      )}
    </AllstateCardV2>
  );
};
